// @ts-nocheck
import { env } from 'env';
import L from 'leaflet';
import 'leaflet.icon.glyph';
import 'leaflet-routing-machine';
import 'leaflet-toolbar';
import { useEffect, useRef, useState } from 'react';
import { Marker, Polygon, Popup, useMap, useMapEvents } from 'react-leaflet';
import { Button, Row, Col, Alert, Input, Label } from 'reactstrap';
import { MapPin, Navigation, Settings, Target, RotateCcw } from 'react-feather';
import { FormattedMessage, useIntl } from 'react-intl';
import ReactDOMServer from 'react-dom/server';
import wkx from 'wkx';

// Extensión de L.LatLng para métodos de geolocalización
declare module 'leaflet' {
  namespace LatLng {
    interface LatLng {
      bearingTo(other: LatLng): number;
      destinationPoint(distance: number, bearing: number): LatLng;
    }
  }
}

// Implementar métodos de geolocalización si no existen
if (!L.LatLng.prototype.bearingTo) {
  L.LatLng.prototype.bearingTo = function(other) {
    const lat1 = this.lat * Math.PI / 180;
    const lat2 = other.lat * Math.PI / 180;
    const deltaLng = (other.lng - this.lng) * Math.PI / 180;
    
    const y = Math.sin(deltaLng) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLng);
    
    const bearing = Math.atan2(y, x);
    return ((bearing * 180 / Math.PI) + 360) % 360;
  };
}

if (!L.LatLng.prototype.destinationPoint) {
  L.LatLng.prototype.destinationPoint = function(distance, bearing) {
    const R = 6371e3; // Radio de la Tierra en metros
    const δ = distance / R;
    const θ = bearing * Math.PI / 180;
    
    const φ1 = this.lat * Math.PI / 180;
    const λ1 = this.lng * Math.PI / 180;
    
    const φ2 = Math.asin(Math.sin(φ1) * Math.cos(δ) + Math.cos(φ1) * Math.sin(δ) * Math.cos(θ));
    const λ2 = λ1 + Math.atan2(Math.sin(θ) * Math.sin(δ) * Math.cos(φ1), Math.cos(δ) - Math.sin(φ1) * Math.sin(φ2));
    
    return new L.LatLng(φ2 * 180 / Math.PI, λ2 * 180 / Math.PI);
  };
}

// Función para interpolar puntos adicionales en la ruta para mayor precisión
const interpolateRoute = (coordinates: number[][], maxDistanceMeters: number = 2.0): number[][] => {
  const interpolated: number[][] = [];
  
  for (let i = 0; i < coordinates.length - 1; i++) {
    const start = coordinates[i];
    const end = coordinates[i + 1];
    
    interpolated.push(start);
    
    // Calcular distancia entre puntos
    const distance = calculateDistance(start[1], start[0], end[1], end[0]);
    
    // Si la distancia es mayor que el máximo, interpolar puntos intermedios
    if (distance > maxDistanceMeters) {
      const steps = Math.ceil(distance / maxDistanceMeters);
      
      for (let step = 1; step < steps; step++) {
        const ratio = step / steps;
        const interpolatedLng = start[0] + (end[0] - start[0]) * ratio;
        const interpolatedLat = start[1] + (end[1] - start[1]) * ratio;
        interpolated.push([interpolatedLng, interpolatedLat]);
      }
    }
  }
  
  // Agregar el último punto
  if (coordinates.length > 0) {
    interpolated.push(coordinates[coordinates.length - 1]);
  }
  
  return interpolated;
};

// Función para calcular distancia entre dos puntos en metros
const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371e3; // Radio de la Tierra en metros
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lng2 - lng1) * Math.PI / 180;

  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
          Math.cos(φ1) * Math.cos(φ2) *
          Math.sin(Δλ/2) * Math.sin(Δλ/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  return R * c;
};

// Componente del toolbar para edición de geocercas de ruta
const RouteGeofenceToolbar = ({ onPlacePoints, onReset, isPlacingPoints, map }) => {
  const intl = useIntl();
  const toolbarRef = useRef(null);

  useEffect(() => {
    if (!map) return;

    const container = map;

    try {
      // @ts-ignore - L.Toolbar2 is not in Leaflet type definitions
      var ImmediateSubAction = L.Toolbar2.Action.extend({
        initialize: function (map, myAction) {
          this.map = map;
          this.myAction = myAction;
          // @ts-ignore - L.Toolbar2 is not in Leaflet type definitions
          L.Toolbar2.Action.prototype.initialize.call(this);
        },
        addHooks: function () {
          this.myAction.disable();
        }
      });

      // Acción para colocar puntos A y B
      // @ts-ignore - L.Toolbar2 is not in Leaflet type definitions
      var placePointsAction = L.Toolbar2.Action.extend({
        options: {
          toolbarIcon: {
            tooltip: intl.formatMessage({ 
              id: 'map.routeGeofence.tooltipPlacePoints', 
              defaultMessage: 'Colocar puntos A y B' 
            }),
            html: ReactDOMServer.renderToStaticMarkup(
              <>
                <Target size={14} />
              </>
            )
          }
        },
        addHooks: () => {
          onPlacePoints();
        }
      });

      // Acción para resetear
      // @ts-ignore - L.Toolbar2 is not in Leaflet type definitions  
      var resetAction = L.Toolbar2.Action.extend({
        options: {
          toolbarIcon: {
            tooltip: intl.formatMessage({ 
              id: 'map.routeGeofence.tooltipReset', 
              defaultMessage: 'Borrar y reiniciar' 
            }),
            html: ReactDOMServer.renderToStaticMarkup(
              <>
                <RotateCcw size={14} />
              </>
            )
          }
        },
        addHooks: () => {
          onReset();
        }
      });

      // @ts-ignore - L.Toolbar2 is not in Leaflet type definitions
      L.Toolbar2.RouteGeofenceToolbar = L.Toolbar2.Control.extend({
        options: {
          actions: [placePointsAction, resetAction]
        }
      });

      // @ts-ignore - L.Toolbar2 is not in Leaflet type definitions
      const toolbar = new L.Toolbar2.RouteGeofenceToolbar({ position: 'topleft' });
      toolbar.addTo(container);
      toolbarRef.current = toolbar;

      return () => {
        if (toolbarRef.current) {
          toolbarRef.current.remove();
        }
      };
    } catch (error) {
      console.error('Error creating route geofence toolbar:', error);
    }
  }, [map, onPlacePoints, onReset, intl]);

  return null;
};

export interface MapRouteGeofenceEditOptions {
  usePopovers?: boolean;
  geofence: any;
  onGeofenceEdit?: Function;
  showGeofence: boolean;
  isMapFullScreen?: boolean;
}

/* -------------------------------------------------------------------------- */
/*           MapRouteGeofenceEdit to edit a route-type geofence in map        */
/* -------------------------------------------------------------------------- */
const MapRouteGeofenceEdit = (props: MapRouteGeofenceEditOptions) => {
  const map = useMap();
  const intl = useIntl();
  const { showGeofence, geofence, onGeofenceEdit, usePopovers, isMapFullScreen = false } = props;

  // Estados para los puntos A y B
  const [pointA, setPointA] = useState<L.LatLng | null>(null);
  const [pointB, setPointB] = useState<L.LatLng | null>(null);
  const [isPlacingPoints, setIsPlacingPoints] = useState(false);
  const [hasStartedSelection, setHasStartedSelection] = useState(false); // Rastrear si se ha iniciado selección manual
  const [placingStep, setPlacingStep] = useState<'A' | 'B' | 'none'>('none'); // A: esperando punto A, B: esperando punto B
  const [routePath, setRoutePath] = useState<L.LatLng[]>([]);
  const [corridorWidth, setCorridorWidth] = useState<number>(100); // Ancho del corredor en metros
  const [corridorPolygon, setCorridorPolygon] = useState<L.LatLng[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  const pointAMarkerRef = useRef(null);
  const pointBMarkerRef = useRef(null);
  const corridorPolygonRef = useRef(null);
  const dragTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const primary = env.VITE_PRIMARY_COLOR;

  // Parsear la geometría WKT inicial si existe
  const parseInitialGeometry = () => {
    if (geofence.geofenceWktString) {
      try {
        const geometry = wkx.Geometry.parse(geofence.geofenceWktString);
        const geoJSON = geometry.toGeoJSON();
        
        if (geoJSON.type === 'Polygon' && geoJSON.coordinates[0].length > 4) {
          // Es un polígono corredor existente, mostrar tal como está
          const coords = geoJSON.coordinates[0];
          const polygonPoints = coords.map(coord => L.latLng(coord[1], coord[0]));
          setCorridorPolygon(polygonPoints);
          
          // Extraer puntos A y B aproximados (primer y punto medio del polígono)
          const quarterIndex = Math.floor(polygonPoints.length / 4);
          setPointA(polygonPoints[0]);
          setPointB(polygonPoints[quarterIndex] || polygonPoints[Math.floor(polygonPoints.length / 2)]);
          
        } else {
          // Para cualquier otro tipo, crear una ruta simple inicial
          // Los puntos A y B se seleccionarán manualmente
          console.log('Geometry type not handled, user needs to select points A and B');
        }
      } catch (error) {
        console.error('Error parsing initial geometry:', error);
      }
    }
  };

  // NO mostrar puntos iniciales automáticamente - solo mostrar geometría existente válida
  useEffect(() => {
    if (
      geofence.geofenceWktString && 
      geofence.isRouteGeofence && 
      geofence.geofenceId && // Solo si tiene ID (geocerca existente, no nueva)
      !pointA && 
      !pointB && 
      !isPlacingPoints
    ) {
      try {
        const geometry = wkx.Geometry.parse(geofence.geofenceWktString);
        const geoJSON = geometry.toGeoJSON();
        
        if (geoJSON.type === 'Polygon' && geoJSON.coordinates[0].length > 4) {
          // Solo mostrar el polígono corredor existente sin puntos A y B
          const coords = geoJSON.coordinates[0];
          const polygonPoints = coords.map(coord => L.latLng(coord[1], coord[0]));
          setCorridorPolygon(polygonPoints);
          console.log('Mostrando geometría existente sin puntos A y B');
        }
      } catch (error) {
        console.error('Error parsing existing geometry:', error);
      }
    }
  }, [geofence.geofenceWktString, geofence.geofenceId]);

  // Efecto para sincronizar coordenadas manuales con marcadores en el mapa
  useEffect(() => {
    // Sincronizar punto A desde coordenadas manuales
    if (geofence.pointALatitude && geofence.pointALongitude && 
        (!pointA || pointA.lat !== geofence.pointALatitude || pointA.lng !== geofence.pointALongitude)) {
      const newPointA = L.latLng(geofence.pointALatitude, geofence.pointALongitude);
      setPointA(newPointA);
      setHasStartedSelection(true); // Marcar como selección iniciada
      
      // Si también tenemos punto B, generar la ruta
      if (geofence.pointBLatitude && geofence.pointBLongitude) {
        const newPointB = L.latLng(geofence.pointBLatitude, geofence.pointBLongitude);
        if (!pointB || pointB.lat !== geofence.pointBLatitude || pointB.lng !== geofence.pointBLongitude) {
          setPointB(newPointB);
        }
        // Pasar los puntos directamente para evitar problemas de estado asíncrono
        generateRoute(newPointA, newPointB);
      }
    }

    // Sincronizar punto B desde coordenadas manuales
    if (geofence.pointBLatitude && geofence.pointBLongitude && 
        (!pointB || pointB.lat !== geofence.pointBLatitude || pointB.lng !== geofence.pointBLongitude)) {
      const newPointB = L.latLng(geofence.pointBLatitude, geofence.pointBLongitude);
      setPointB(newPointB);
      setHasStartedSelection(true); // Marcar como selección iniciada
      
      // Si también tenemos punto A, generar la ruta
      if (geofence.pointALatitude && geofence.pointALongitude) {
        const newPointA = L.latLng(geofence.pointALatitude, geofence.pointALongitude);
        if (!pointA || pointA.lat !== geofence.pointALatitude || pointA.lng !== geofence.pointALongitude) {
          setPointA(newPointA);
        }
        // Pasar los puntos directamente para evitar problemas de estado asíncrono
        generateRoute(newPointA, newPointB);
      }
    }
  }, [geofence.pointALatitude, geofence.pointALongitude, geofence.pointBLatitude, geofence.pointBLongitude]);

    // Funciones del toolbar
  const handlePlacePoints = () => {
    setIsPlacingPoints(true);
    setHasStartedSelection(true); // Marcar que se ha iniciado la selección manual
    setPlacingStep('A');
    // Limpiar puntos existentes si hay
    setPointA(null);
    setPointB(null);
    setCorridorPolygon([]);
    setRoutePath([]);
  };

  const handleReset = () => {
    setIsPlacingPoints(false);
    setHasStartedSelection(false); // Resetear también el estado de selección iniciada
    setPlacingStep('none');
    setPointA(null);
    setPointB(null);
    setCorridorPolygon([]);
    setRoutePath([]);
    
    // Actualizar la geocerca con datos vacíos incluyendo coordenadas manuales
    if (onGeofenceEdit) {
      onGeofenceEdit({
        ...geofence,
        shape: 'P', // Mantener como polígono
        geofenceWktString: undefined,
        radius: 0,
        isRouteGeofence: true, // Mantener el flag de ruta
        pointALatitude: undefined,
        pointALongitude: undefined,
        pointBLatitude: undefined,
        pointBLongitude: undefined
      });
    }
  };

  // Manejar clicks en el mapa para seleccionar puntos secuencialmente
  useEffect(() => {
    const handleMapClick = (e: L.LeafletMouseEvent) => {
      if (!isPlacingPoints) return;
      
      // Prevenir propagación para asegurar que se capture el evento correctamente
      e.originalEvent?.stopPropagation();
      e.originalEvent?.preventDefault();

      console.log('Click detectado en:', e.latlng, 'Paso:', placingStep);

      if (placingStep === 'A') {
        setPointA(e.latlng);
        setPlacingStep('B');
        
        // Actualizar coordenadas manuales para punto A (preservando las del punto B si existen)
        if (onGeofenceEdit) {
          onGeofenceEdit({
            ...geofence,
            pointALatitude: e.latlng.lat,
            pointALongitude: e.latlng.lng,
            pointBLatitude: pointB?.lat || geofence.pointBLatitude,
            pointBLongitude: pointB?.lng || geofence.pointBLongitude
          });
        }
        
        console.log('Punto A colocado en:', e.latlng);
      } else if (placingStep === 'B') {
        console.log('Punto B colocado en:', e.latlng);
        
        // Guardar primero el punto B exactamente donde se hizo click
        const exactPointB = L.latLng(e.latlng.lat, e.latlng.lng);
        setPointB(exactPointB);
        setPlacingStep('none');
        setIsPlacingPoints(false);
        
        console.log('Estado ANTES de actualizar - PointA:', pointA, 'PointB nuevo:', exactPointB);
        
        // Actualizar coordenadas manuales para punto B (incluyendo las del punto A)
        if (onGeofenceEdit) {
          onGeofenceEdit({
            ...geofence,
            pointALatitude: pointA?.lat,
            pointALongitude: pointA?.lng,
            pointBLatitude: exactPointB.lat,
            pointBLongitude: exactPointB.lng
          });
        }
        
        // Generar la ruta entre los dos puntos usando las coordenadas exactas
        // IMPORTANTE: Pasar exactPointB directamente, no usar el estado pointB
        generateRoute(pointA, exactPointB);
      }
    };

    if (isPlacingPoints) {
      // Usar un z-index alto para asegurar que el evento se capture correctamente
      map.on('click', handleMapClick);
      map.getContainer().style.cursor = 'crosshair';
      map.getContainer().style.zIndex = '1000';
    } else {
      map.off('click', handleMapClick);
      map.getContainer().style.cursor = '';
      map.getContainer().style.zIndex = '';
    }

    return () => {
      map.off('click', handleMapClick);
      map.getContainer().style.cursor = '';
      map.getContainer().style.zIndex = '';
    };
  }, [isPlacingPoints, placingStep, pointA]);

  // Función para crear un polígono corredor de alta precisión alrededor de una línea
  const createCorridorPolygon = (pathCoords: L.LatLng[], width: number): L.LatLng[] => {
    if (pathCoords.length < 2) return [];

    const halfWidth = width / 2;
    const leftSide: L.LatLng[] = [];
    const rightSide: L.LatLng[] = [];

    // NO simplificar la ruta - usar todos los puntos para máxima precisión
    const fullPath = pathCoords;
    
    console.log(`Creando corredor de alta precisión con ${fullPath.length} puntos`);

    for (let i = 0; i < fullPath.length; i++) {
      const currentPoint = fullPath[i];
      let bearing = 0;
      
      if (i === 0 && fullPath.length > 1) {
        // Primer punto: usar dirección hacia el siguiente punto
        bearing = currentPoint.bearingTo(fullPath[i + 1]);
      } else if (i === fullPath.length - 1) {
        // Último punto: usar dirección desde el punto anterior
        bearing = fullPath[i - 1].bearingTo(currentPoint);
      } else {
        // Punto intermedio: usar dirección hacia el siguiente punto
        bearing = currentPoint.bearingTo(fullPath[i + 1]);
      }

      // Calcular puntos perpendiculares (90 grados a la izquierda y derecha)
      const leftBearing = (bearing - 90 + 360) % 360;
      const rightBearing = (bearing + 90) % 360;

      const leftPoint = currentPoint.destinationPoint(halfWidth, leftBearing);
      const rightPoint = currentPoint.destinationPoint(halfWidth, rightBearing);

      leftSide.push(leftPoint);
      rightSide.push(rightPoint);
    }

    // Crear el polígono: lado izquierdo + lado derecho invertido
    const corridor: L.LatLng[] = [];
    corridor.push(...leftSide);
    corridor.push(...rightSide.reverse());
    
    // Cerrar el polígono volviendo al primer punto
    if (corridor.length > 0) {
      corridor.push(corridor[0]);
    }

    return corridor;
  };

  // Generar corredor poligonal entre punto A y punto B
  const generateRoute = async (startPoint: L.LatLng | null, endPoint: L.LatLng | null) => {
    if (!startPoint || !endPoint) return;

    console.log('Generando ruta desde:', startPoint, 'hacia:', endPoint);

    try {
      // Usar servicio de routing con máxima precisión y detalle
      const response = await fetch(
        `https://router.project-osrm.org/route/v1/driving/${startPoint.lng},${startPoint.lat};${endPoint.lng},${endPoint.lat}?overview=full&geometries=geojson&steps=true&annotations=true&generate_hints=false`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.routes && data.routes[0] && data.routes[0].geometry) {
          let coordinates = data.routes[0].geometry.coordinates;
          
          // Calcular distancia total de la ruta para determinar precisión óptima
          let totalDistance = 0;
          for (let i = 0; i < coordinates.length - 1; i++) {
            const dist = calculateDistance(coordinates[i][1], coordinates[i][0], coordinates[i+1][1], coordinates[i+1][0]);
            totalDistance += dist;
          }
          
          // Determinar densidad de interpolación basada en la distancia total
          let interpolationInterval = 3.0; // Por defecto 3 metros
          if (totalDistance < 500) {
            interpolationInterval = 1.0; // Para rutas cortas, máxima precisión (1 metro)
          } else if (totalDistance < 2000) {
            interpolationInterval = 2.0; // Para rutas medianas, alta precisión (2 metros)
          }
          
          // Mejorar la precisión: Si hay pocos puntos, interpolar más puntos entre ellos
          if (coordinates.length < 100 || totalDistance / coordinates.length > interpolationInterval) {
            console.log(`Interpolando ruta de ${totalDistance.toFixed(0)}m con intervalos de ${interpolationInterval}m`);
            coordinates = interpolateRoute(coordinates, interpolationInterval);
          }
          
          let pathCoords = coordinates.map(coord => L.latLng(coord[1], coord[0]));
          
          // IMPORTANTE: Forzar que los puntos de inicio y fin sean exactamente los que el usuario colocó
          pathCoords[0] = startPoint;
          pathCoords[pathCoords.length - 1] = endPoint;
          
          console.log('Ruta generada con', pathCoords.length, 'puntos (mejorada para máxima precisión)');
          console.log('Punto final forzado a:', endPoint);
          console.log('Estado actual - PointA:', startPoint, 'PointB:', endPoint);
          
          setRoutePath(pathCoords);
          generateCorridorGeofence(pathCoords, startPoint, endPoint);
          return;
        }
      }
      
      // Si falla el routing, crear línea recta
      console.log('Routing service failed, using straight line');
      generateStraightLine(startPoint, endPoint);
      
    } catch (error) {
      console.error('Error generating route:', error);
      // Fallback: crear una línea recta
      generateStraightLine(startPoint, endPoint);
    }
  };

  // Función auxiliar para crear línea recta
  const generateStraightLine = (startPoint: L.LatLng, endPoint: L.LatLng) => {
    const pathCoords = [startPoint, endPoint];
    setRoutePath(pathCoords);
    generateCorridorGeofence(pathCoords, startPoint, endPoint);
  };

  // Generar la geocerca corredor poligonal
  const generateCorridorGeofence = (pathCoords: L.LatLng[], currentPointA?: L.LatLng, currentPointB?: L.LatLng) => {
    // Usar los puntos pasados como parámetro si están disponibles, sino usar el estado
    const effectivePointA = currentPointA || pointA;
    const effectivePointB = currentPointB || pointB;
    
    console.log('Generando corredor con puntos A y B:', effectivePointA, effectivePointB);
    const polygon = createCorridorPolygon(pathCoords, corridorWidth);
    setCorridorPolygon(polygon);
    
    if (polygon.length > 0) {
      // Crear la cadena WKT para el polígono
      const wktCoords = polygon.map(coord => `${coord.lng} ${coord.lat}`).join(', ');
      const wktString = `POLYGON ((${wktCoords}))`;
      
      console.log('Llamando onGeofenceEdit, puntos antes:', effectivePointA, effectivePointB);
      // Notificar el cambio
      if (onGeofenceEdit) {
        onGeofenceEdit({
          ...geofence,
          shape: 'P', // Asegurar que se mantenga como polígono
          geofenceWktString: wktString,
          corridorWidth: corridorWidth,
          isRouteGeofence: true, // Mantener el flag de ruta
          // Agregar coordenadas de los puntos A y B (usando los efectivos)
          pointALatitude: effectivePointA?.lat,
          pointALongitude: effectivePointA?.lng,
          pointBLatitude: effectivePointB?.lat,
          pointBLongitude: effectivePointB?.lng
        });
      }
      console.log('Puntos después de onGeofenceEdit:', effectivePointA, effectivePointB);
    }
  };

  // Función con debounce para regenerar ruta durante arrastre
  const debouncedGenerateRoute = (startPoint: L.LatLng, endPoint: L.LatLng) => {
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
    }
    
    dragTimeoutRef.current = setTimeout(() => {
      generateRoute(startPoint, endPoint);
      setIsDragging(false);
    }, 500); // 500ms de debounce
  };

  // Actualizar corredor cuando cambie el ancho
  useEffect(() => {
    if (routePath.length > 0) {
      generateCorridorGeofence(routePath, pointA, pointB);
    }
  }, [corridorWidth]);

  // Limpiar timeout al desmontar
  useEffect(() => {
    return () => {
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }
    };
  }, []);

  // Iconos para marcadores temporales durante colocación - Alineados con el estilo de la plataforma
  const getPlacingMarkerIcon = (step: 'A' | 'B') => L.divIcon({
    className: 'custom-div-icon',
    html: `<div class='marker-pin' style='background-color:${step === 'A' ? '#28a745' : '#dc3545'}'></div>
           <i class='icon-${step === 'A' ? 'Origin' : 'Destination'}' style='width: 20px; font-size: 20px; color: white;'></i>`,
    iconSize: [30, 42],
    iconAnchor: [15, 42],
    popupAnchor: [0, -35]
  });

  return (
    <>
      {/* Estilos CSS para el slider */}
      <style>
        {`
          .corridor-width-slider::-webkit-slider-thumb {
            appearance: none;
            height: 18px;
            width: 18px;
            background: #3498db;
            border-radius: 50%;
            cursor: pointer;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
            border: 2px solid white;
          }
          
          .corridor-width-slider::-moz-range-thumb {
            height: 18px;
            width: 18px;
            background: #3498db;
            border-radius: 50%;
            cursor: pointer;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
            border: 2px solid white;
          }
          
          .corridor-width-slider::-webkit-slider-track {
            height: 6px;
            background: #ddd;
            border-radius: 3px;
          }
          
          .corridor-width-slider::-moz-range-track {
            height: 6px;
            background: #ddd;
            border-radius: 3px;
            border: none;
          }
        `}
      </style>
      
      {showGeofence && geofence.isRouteGeofence && (
        <div>
          {/* Toolbar */}
          <RouteGeofenceToolbar 
            onPlacePoints={handlePlacePoints}
            onReset={handleReset}
            isPlacingPoints={isPlacingPoints}
            map={map}
          />

          {/* Indicador de estado */}
          {isPlacingPoints && (
            <div style={{ 
              position: 'absolute', 
              top: '10px', 
              right: '10px', 
              zIndex: 1000, 
              background: placingStep === 'A' ? 'rgba(40, 167, 69, 0.9)' : 'rgba(220, 53, 69, 0.9)', 
              color: 'white',
              padding: '8px 12px', 
              borderRadius: '4px', 
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              {placingStep === 'A' && '📍 Haz clic para colocar el punto A'}
              {placingStep === 'B' && '📍 Haz clic para colocar el punto B'}
            </div>
          )}

          {/* MARCADORES PERMANENTES A y B para verificación - Solo después de iniciar selección manual */}
          {pointA && hasStartedSelection && (
            <Marker
              position={pointA}
              icon={L.divIcon({
                className: 'custom-div-icon',
                html: `<div class='marker-pin' style='background-color:#28a745'></div><i class='icon-Marker1' style='width: 20px; font-size: 20px; color:white;'></i>`,
                iconSize: [30, 42],
                iconAnchor: [15, 42],
                popupAnchor: [0, -35]
              })}
              interactive={false}
              zIndexOffset={1000} // Muy arriba para ser visible siempre
            />
          )}
          
          {pointB && hasStartedSelection && (
            <Marker
              position={pointB}
              icon={L.divIcon({
                className: 'custom-div-icon',
                html: `<div class='marker-pin' style='background-color:#dc3545'></div><i class='icon-Marker1' style='width: 20px; font-size: 20px; color:white;'></i>`,
                iconSize: [30, 42],
                iconAnchor: [15, 42],
                popupAnchor: [0, -35]
              })}
              interactive={false}
              zIndexOffset={1000} // Muy arriba para ser visible siempre
            />
          )}

          {/* Control de ancho del corredor y información de puntos */}
          {pointA && pointB && !isPlacingPoints && (
            <div 
              style={{ 
                position: 'absolute', 
                top: '10px', 
                right: '10px', 
                zIndex: 1000, 
                background: 'white', 
                padding: '12px', 
                borderRadius: '8px', 
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                minWidth: '220px',
                fontSize: '11px',
                border: '1px solid #e0e0e0'
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onMouseMove={(e) => e.stopPropagation()}
              onMouseUp={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
            >
              <div style={{ fontSize: '12px', textAlign: 'center', marginBottom: '4px', fontWeight: 'bold' }}>
                Ancho del corredor: {corridorWidth}m
              </div>
              <input
                type="range"
                min="20"
                max="500"
                step="10"
                value={corridorWidth}
                onChange={(e) => setCorridorWidth(parseInt(e.target.value))}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  map.dragging.disable(); // Deshabilitar arrastre del mapa mientras se usa el slider
                }}
                onMouseUp={(e) => {
                  e.stopPropagation();
                  map.dragging.enable(); // Rehabilitar arrastre del mapa
                }}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  map.dragging.disable(); // Para dispositivos táctiles
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  map.dragging.enable(); // Para dispositivos táctiles
                }}
                style={{ 
                  width: '100%',
                  height: '6px', 
                  marginBottom: '8px',
                  background: 'linear-gradient(to right, #ddd 0%, #ddd 100%)',
                  borderRadius: '3px',
                  outline: 'none',
                  cursor: 'pointer',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none'
                }}
                className="corridor-width-slider"
              />
              <div style={{ borderTop: '1px solid #eee', paddingTop: '4px' }}>
                <div><span style={{color: '#28a745', fontWeight: 'bold'}}>A:</span> {pointA.lat.toFixed(6)}, {pointA.lng.toFixed(6)}</div>
                <div><span style={{color: '#dc3545', fontWeight: 'bold'}}>B:</span> {pointB.lat.toFixed(6)}, {pointB.lng.toFixed(6)}</div>
              </div>
            </div>
          )}



          {/* Marcadores arrastrables para edición (cuando no se están colocando puntos y se ha iniciado selección) */}
          {!isPlacingPoints && pointA && hasStartedSelection && (
            <Marker
              ref={pointAMarkerRef}
              position={pointA}
              icon={L.divIcon({
                className: 'custom-div-icon',
                html: `<div style="width: 15px; height: 15px; background: rgba(40, 167, 69, 0.8); border: 2px solid white; border-radius: 50%; cursor: move;"></div>`,
                iconSize: [15, 15],
                iconAnchor: [7, 7]
              })}
              draggable={true}
              opacity={0.8}
              zIndexOffset={500}
              eventHandlers={{
                dragstart: () => {
                  setIsDragging(true);
                },
                drag: (e) => {
                  const newPosition = e.target.getLatLng();
                  setPointA(newPosition);
                  
                  // Actualizar coordenadas manuales (preservando punto B)
                  if (onGeofenceEdit) {
                    onGeofenceEdit({
                      ...geofence,
                      pointALatitude: newPosition.lat,
                      pointALongitude: newPosition.lng,
                      pointBLatitude: pointB?.lat || geofence.pointBLatitude,
                      pointBLongitude: pointB?.lng || geofence.pointBLongitude
                    });
                  }
                  
                  if (pointB) {
                    debouncedGenerateRoute(newPosition, pointB);
                  }
                },
                dragend: (e) => {
                  const newPosition = e.target.getLatLng();
                  setPointA(newPosition);
                  
                  // Actualizar coordenadas manuales (preservando punto B)
                  if (onGeofenceEdit) {
                    onGeofenceEdit({
                      ...geofence,
                      pointALatitude: newPosition.lat,
                      pointALongitude: newPosition.lng,
                      pointBLatitude: pointB?.lat || geofence.pointBLatitude,
                      pointBLongitude: pointB?.lng || geofence.pointBLongitude
                    });
                  }
                  
                  if (pointB) {
                    if (dragTimeoutRef.current) {
                      clearTimeout(dragTimeoutRef.current);
                    }
                    generateRoute(newPosition, pointB);
                  }
                  setIsDragging(false);
                }
              }}
            />
          )}

          {!isPlacingPoints && pointB && hasStartedSelection && (
            <Marker
              ref={pointBMarkerRef}
              position={pointB}
              icon={L.divIcon({
                className: 'custom-div-icon',
                html: `<div style="width: 15px; height: 15px; background: rgba(220, 53, 69, 0.8); border: 2px solid white; border-radius: 50%; cursor: move;"></div>`,
                iconSize: [15, 15],
                iconAnchor: [7, 7]
              })}
              draggable={true}
              opacity={0.8}
              zIndexOffset={500}
              eventHandlers={{
                dragstart: () => {
                  setIsDragging(true);
                },
                drag: (e) => {
                  const newPosition = e.target.getLatLng();
                  setPointB(newPosition);
                  
                  // Actualizar coordenadas manuales (preservando punto A)
                  if (onGeofenceEdit) {
                    onGeofenceEdit({
                      ...geofence,
                      pointALatitude: pointA?.lat || geofence.pointALatitude,
                      pointALongitude: pointA?.lng || geofence.pointALongitude,
                      pointBLatitude: newPosition.lat,
                      pointBLongitude: newPosition.lng
                    });
                  }
                  
                  if (pointA) {
                    debouncedGenerateRoute(pointA, newPosition);
                  }
                },
                dragend: (e) => {
                  const newPosition = e.target.getLatLng();
                  setPointB(newPosition);
                  
                  // Actualizar coordenadas manuales (preservando punto A)
                  if (onGeofenceEdit) {
                    onGeofenceEdit({
                      ...geofence,
                      pointALatitude: pointA?.lat || geofence.pointALatitude,
                      pointALongitude: pointA?.lng || geofence.pointALongitude,
                      pointBLatitude: newPosition.lat,
                      pointBLongitude: newPosition.lng
                    });
                  }
                  
                  if (pointA) {
                    if (dragTimeoutRef.current) {
                      clearTimeout(dragTimeoutRef.current);
                    }
                    generateRoute(pointA, newPosition);
                  }
                  setIsDragging(false);
                }
              }}
            />
          )}

          {/* Polígono corredor de la geocerca */}
          {corridorPolygon.length > 0 && (
            <Polygon
              ref={corridorPolygonRef}
              positions={corridorPolygon}
              pathOptions={{ 
                color: geofence.color || primary, 
                weight: 2,
                opacity: 0.8,
                fillColor: geofence.color || primary,
                fillOpacity: 0.2
              }}
            >
              {usePopovers && (
                <Popup>
                  <b style={{ fontSize: '12px' }}>
                    {geofence.geofenceName || <FormattedMessage id="geofences.route.corridor" defaultMessage="Corredor" />}
                    <br />
                    <small>
                      <FormattedMessage id="geofences.route.width" defaultMessage="Ancho" />: {corridorWidth}m
                    </small>
                  </b>
                </Popup>
              )}
            </Polygon>
          )}
        </div>
      )}
    </>
  );
};

MapRouteGeofenceEdit.defaultProps = {
  geofence: {},
  showGeofence: false,
  isMapFullScreen: false,
  usePopovers: true
};

export default MapRouteGeofenceEdit;